import { Plus, Tags } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import {
	useClassificationList,
	useCreateClassification,
	useDeleteClassification,
	useUpdateClassification,
} from '@/api/queries/catalog-queries'
import {
	type Classification,
	ClassificationFormContent,
} from '@/components/administration'
import {
	AdminCardPage,
	ResponsiveFormModal,
	SearchInput,
} from '@/components/commons'
import { GroupedList } from '@/components/grouped-list'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { adminBackRoute } from '@/constants/admin-menu'
import { ROUTES } from '@/constants/routes'
import type { ClassificationWithSubsOutput } from '@/types'
import { getApiErrorMessage } from '@/utils/api-error'

export const ClassificationsPage = () => {
	const navigate = useNavigate()
	const { t } = useTranslation()

	const { data, isLoading } = useClassificationList()
	const createMutation = useCreateClassification()
	const updateMutation = useUpdateClassification()
	const deleteMutation = useDeleteClassification()

	const classifications =
		data?.items.map((data: ClassificationWithSubsOutput) => ({
			id: data.id,
			name: data.name,
			score: data.score,
			spheraName: data.spheraName,
			children: data.subclassifications.map((sub) => ({
				id: sub.id,
				name: sub.name,
			})),
		})) || []
	const [search, setSearch] = useState('')

	// Form dialog/drawer state
	const [formOpen, setFormOpen] = useState(false)
	const [editing, setEditing] = useState<Classification | undefined>(undefined)
	const [deletingItem, setDeletingItem] = useState<{
		id: string
		name: string
	} | null>(null)

	/* ── Filtering ── */
	const filtered = classifications.filter((cls) => {
		const q = search.toLowerCase()
		return (
			cls.name.toLowerCase().includes(q) ||
			cls.children.some((sub) => sub.name.toLowerCase().includes(q))
		)
	})

	/* ── CRUD handlers ── */
	const handleOpenCreate = () => {
		setEditing(undefined)
		setFormOpen(true)
	}

	const handleOpenEdit = (cls: Classification) => {
		setEditing(cls)
		setFormOpen(true)
	}

	const handleSave = async ({
		name,
		spheraName,
		subclassifications,
	}: {
		name: string
		spheraName: string | null
		subclassifications: string[]
	}) => {
		try {
			if (editing) {
				const updatedSubclassifications = subclassifications.map((name, i) => ({
					id: editing.children[i]?.id,
					name,
				}))
				await updateMutation.mutateAsync({
					id: editing.id,
					data: {
						name,
						spheraName,
						subclassifications: updatedSubclassifications,
					},
				})
				toast.success(t('administration.classifications.editSuccess', { name }))
			} else {
				const updatedSubclassifications = subclassifications.map((name) => ({
					name,
				}))
				await createMutation.mutateAsync({
					name,
					spheraName,
					subclassifications: updatedSubclassifications,
				})
				toast.success(t('administration.classificationsForm.saveSuccess'))
			}
			setFormOpen(false)
			setEditing(undefined)
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
	}

	const handleDeleteClassification = async (id: string, name: string) => {
		try {
			await deleteMutation.mutateAsync(id)
			toast.success(
				t('administration.classifications.deleteClassificationSuccess', {
					name,
				}),
			)
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
	}

	const handleConfirmDelete = async () => {
		if (!deletingItem) {
			return
		}

		await handleDeleteClassification(deletingItem.id, deletingItem.name)
		setDeletingItem(null)
	}

	/* ── Responsive form ── */
	const formTitle = editing
		? t('administration.classifications.editClassification')
		: t('administration.classifications.createNew')

	return (
		<>
			<AdminCardPage
				icon={<Tags className="size-5" />}
				title={t('administration.classifications.title')}
				subtitle={t('administration.classifications.subtitle')}
				onBack={() =>
					navigate(adminBackRoute(ROUTES.ADMINISTRATION.CLASSIFICATIONS))
				}
				footerText={`${filtered.length} ${
					filtered.length === 1
						? t('administration.classifications.countSingular')
						: t('administration.classifications.countPlural')
				}`}
				actions={
					<Button
						onClick={handleOpenCreate}
						className="gap-2 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90 w-full sm:w-auto"
					>
						<Plus className="size-4" />
						<span>{t('administration.classifications.createNew')}</span>
					</Button>
				}
				modal={
					<ResponsiveFormModal
						open={formOpen}
						onOpenChange={setFormOpen}
						title={formTitle}
					>
						<ClassificationFormContent
							mode={editing ? 'edit' : 'create'}
							initial={editing}
							onSave={handleSave}
							onClose={() => setFormOpen(false)}
						/>
					</ResponsiveFormModal>
				}
			>
				<SearchInput
					value={search}
					onChange={setSearch}
					placeholder={t('administration.classifications.searchPlaceholder')}
				/>
				{isLoading ? (
					<div className="p-6 text-center text-sm text-muted-foreground">
						{t('administration.classifications.loading')}
					</div>
				) : (
					<GroupedList
						groups={filtered}
						search={search}
						badgeClass="bg-kaefer-medium-grey/15 text-kaefer-medium-grey"
						childBadgeClass="bg-kaefer-medium-grey/10 text-kaefer-medium-grey"
						onEditGroup={handleOpenEdit}
						onDeleteGroup={(id, name) => setDeletingItem({ id, name })}
						emptyMessage={t('administration.classifications.noResults')}
						emptyIcon={<Tags className="size-8 text-kaefer-light-silver" />}
					/>
				)}
			</AdminCardPage>

			<AlertDialog
				open={!!deletingItem}
				onOpenChange={(open) => {
					if (!open) {
						setDeletingItem(null)
					}
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
						<AlertDialogDescription>
							Deseja realmente deletar a classificação
							{deletingItem?.name ? ` "${deletingItem.name}"` : ''}?
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
						<AlertDialogAction onClick={handleConfirmDelete}>
							{t('common.delete')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}
